/**
 * UID-116 (ADR-0051) / UID-292 — the web-side money-safe variant derivations
 * (`applyVariantRatio` / `committedDateForVariant`) are now re-exported from the
 * single source of truth in `@misumi/shared` (`rfq-quote-variants`), rather than
 * kept as a hand-mirrored second implementation of `apps/api`'s copy.
 *
 * The QuotedLinesPanel still previews a variant's derived price and committed
 * date BEFORE the customer picks "Add to cart" (the server only re-derives once
 * the priced answer is built) — but that preview now runs the EXACT same code
 * the server does, so it can no longer lie about what lands in the cart. Every
 * existing consumer keeps importing these from `./variant-pricing` unchanged.
 *
 * ADR-0004 money-safety (BigInt integer math, never a bare float `*` on the
 * decimal strings) and the ADR-0051 boundary (scales a base line's Price.amount
 * ONLY — never a flat ChargeLine NRE/tooling amount) are preserved in the shared
 * implementation.
 */
export { applyVariantRatio, committedDateForVariant } from '@misumi/shared'

import type { QuoteVariant } from '@misumi/shared'

/**
 * ADR-0131 A2 amendment — carry the accepted ADR-0051 PRICING VARIANT into the CART's delivery
 * option on RFQ→cart, so shipping source flows through the existing delivery-option path
 * identically on the Cart page and Checkout (the accepted variant is not persisted server-side,
 * so this is how the buyer's chosen pricing region reaches shipping without a contract/migration
 * change — see ADR-0131 amendment). The pricing variant sets PART price; this maps it to the
 * delivery-option region that the money seams already turn into a shipping SOURCE:
 *   - `us`               → `do-expedited`        (US → domestic → Overnight $242.46)
 *   - `prefer_us_blend`  → `do-standard`         (North America → domestic → Ground $210.20)
 *   - `offshore`         → `do-overseas`         (Overseas → overseas → Ground $181.53)
 *   - `offshore_economy` → `do-overseas-economy` (Overseas → overseas → Ground $156.77,
 *                          genuinely cheaper than `do-overseas`)
 *
 * UID-456 — `offshore_economy` used to share `do-overseas` with `offshore`. That was harmless
 * while nothing rendered the 4 variants as 4 separately-selectable rows; once the cart's
 * `CartQuoteOptionSelector` did exactly that, the shared id made the REVERSE lookup (delivery-
 * option id → which of the 4 rows was actually chosen) ambiguous. `do-overseas-economy`
 * (`apps/api/.../demo-pricing-catalog.ts`) is now a real, distinct 4th delivery option, so
 * this mapping is an exact 1:1 with no shared ids left, AND it is genuinely priced below
 * `do-overseas`: `resolveShippingCost` (used by `deriveCartSummary`/`presentCheckout`) resolves
 * this id's own price directly rather than collapsing it onto `do-overseas`'s via the plain
 * service+source seam (see that fixture's own doc comment for why the plain seam alone would
 * have discarded this id's distinct rate).
 */
const VARIANT_TO_DELIVERY_OPTION: Readonly<Record<QuoteVariant['id'], string>> = {
  us: 'do-expedited',
  prefer_us_blend: 'do-standard',
  offshore: 'do-overseas',
  offshore_economy: 'do-overseas-economy',
}

/** The cart delivery-option id that carries a pricing variant's shipping SOURCE, or `undefined`
 *  for an unknown/absent variant (the caller then leaves the cart's delivery option unchanged). */
export function deliveryOptionForVariant(
  variantId: QuoteVariant['id'] | null | undefined,
): string | undefined {
  return variantId == null ? undefined : VARIANT_TO_DELIVERY_OPTION[variantId]
}

/** The REVERSE of {@link deliveryOptionForVariant} — which pricing variant a cart's persisted
 *  delivery-option id corresponds to, or `undefined` for an unknown/absent id. UID-456: now
 *  exact/unambiguous (every variant has its own distinct delivery-option id), so this is a
 *  safe, lossless inverse of {@link VARIANT_TO_DELIVERY_OPTION} rather than a guess. Lets a
 *  cart-side consumer (`CartQuoteOptionSelector`) initialize its default-checked row from the
 *  cart's REAL persisted selection instead of always defaulting to the first canonical variant. */
export function variantForDeliveryOption(
  deliveryOptionId: string | null | undefined,
): QuoteVariant['id'] | undefined {
  if (deliveryOptionId == null) return undefined
  const entry = Object.entries(VARIANT_TO_DELIVERY_OPTION).find(([, id]) => id === deliveryOptionId)
  return entry?.[0] as QuoteVariant['id'] | undefined
}
